import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Packages
import { Button, Popconfirm, Select, Space, Table, Tag, Typography } from 'antd';
import { CrownFilled, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Components
import PackageForm from '../packages/PackageForm';

// Actions
import { get_all_packages, package_update } from '../../../redux/actions/packagesActions';

// Utils
import { object_ref_id } from '../../../utils/world_medals';

const cardStyle = {
	background: '#fff',
	border: '1px solid #efedec',
	borderRadius: 16,
	boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
	padding: '24px 26px',
	marginBottom: 20
};

const sectionIconStyle = {
	width: 30,
	height: 30,
	borderRadius: 9,
	background: '#FFEDE9',
	color: '#B75D46',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	flex: 'none'
};

const WorldPacksCard = (props) => {
	const { messageApi, worldId } = props;

	const { t } = useTranslation();

	const dispatch = useDispatch();

	const { all_packages, package_update_loading } = useSelector(state => state.packages);

	const { all_recipes } = useSelector(state => state.recipes);

	const [packFormOpen, setPackFormOpen] = useState(false);
	const [packPreset, setPackPreset] = useState(null);
	const [selectedWorldPack, setSelectedWorldPack] = useState(null);
	const [selectedRecipes, setSelectedRecipes] = useState([]);

	const packs = all_packages?.packages ?? [];

	const worldPacks = packs.filter((pack) => (
		(pack.worlds ?? []).some((world) => object_ref_id(world) === worldId)
	));

	const worldPackOptions = packs
		.filter((pack) => (
			(pack.worlds ?? []).length > 0 &&
			!(pack.worlds ?? []).some((world) => object_ref_id(world) === worldId)
		))
		.map((pack) => ({ label: pack.name, value: object_ref_id(pack._id) }));

	const recipePacks = packs.filter((pack) => (pack.recipes ?? []).length > 0);

	const worldRecipes = (all_recipes?.recipes ?? [])
		.filter((recipe) => object_ref_id(recipe.world) === worldId)
		.sort((a, b) => (a.index ?? 0) - (b.index ?? 0));

	const recipe_pack = (recipeId) => recipePacks.find((pack) => (
		(pack.recipes ?? []).some((recipe) => object_ref_id(recipe) === recipeId)
	));

	const refetch = () => dispatch(get_all_packages({ skip: 0, limit: 0 }));

	// package_update expects the FULL pack body: preserve every field and
	// override only the membership list that changed.
	const pack_body = (pack, overrides) => ({
		name: pack.name,
		description: pack.description,
		star: pack.star,
		featured: pack.featured,
		value: pack.value,
		worlds: (pack.worlds ?? []).map(object_ref_id),
		recipes: (pack.recipes ?? []).map(object_ref_id),
		...overrides
	});

	const update_pack = (pack, overrides) => {
		dispatch(package_update(
			messageApi, object_ref_id(pack._id), pack_body(pack, overrides), refetch
		));
	};

	const removeWorld = (pack) => {
		const worlds = (pack.worlds ?? [])
			.map(object_ref_id)
			.filter((id) => id !== worldId);

		// Leaving its last pack sends the world back into the star chain.
		if (worldPacks.length === 1) {
			messageApi.warning(t('worlds.details.becameFreeWarning'), 6);
		}

		update_pack(pack, { worlds });
	};

	const addWorld = () => {
		const pack = packs.find((p) => object_ref_id(p._id) === selectedWorldPack);
		if (!pack) return;

		update_pack(pack, {
			worlds: [...(pack.worlds ?? []).map(object_ref_id), worldId]
		});
		setSelectedWorldPack(null);
	};

	const removeRecipe = (recipeId, pack) => {
		update_pack(pack, {
			recipes: (pack.recipes ?? [])
				.map(object_ref_id)
				.filter((id) => id !== recipeId)
		});
	};

	const addRecipe = (recipeId, packId) => {
		const pack = packs.find((p) => object_ref_id(p._id) === packId);
		if (!pack) return;

		update_pack(pack, {
			recipes: [...(pack.recipes ?? []).map(object_ref_id), recipeId]
		});
	};

	const openWorldPackForm = () => {
		setPackPreset({ content: 0, worlds: [worldId] });
		setPackFormOpen(true);
	};

	const openRecipePackForm = () => {
		setPackPreset({ content: 1, recipes: selectedRecipes });
		setPackFormOpen(true);
		setSelectedRecipes([]);
	};

	const recipeColumns = [
		{
			key: 'name',
			title: t('form.label.name'),
			dataIndex: 'name'
		},
		{
			key: 'pack',
			title: t('worlds.details.recipePackColumn'),
			render: (_, record) => {
				const pack = recipe_pack(object_ref_id(record._id));
				return pack
					?	<Tag color='gold'>{pack.name}</Tag>
					:	<Typography.Text type='secondary'>—</Typography.Text>;
			}
		},
		{
			key: 'actions',
			render: (_, record) => {
				const recipeId = object_ref_id(record._id);
				const pack = recipe_pack(recipeId);

				if (pack) {
					return (
						<Popconfirm
							title={t('worlds.details.removeRecipeFromPackConfirm', { name: record.name })}
							onConfirm={() => removeRecipe(recipeId, pack)}
						>
							<Button size='small' danger loading={package_update_loading}>
								{t('worlds.details.removeFromPack')}
							</Button>
						</Popconfirm>
					);
				}

				return (
					<Select
						size='small'
						style={{ minWidth: 160 }}
						placeholder={t('worlds.details.addRecipeToPackPlaceholder')}
						value={null}
						options={recipePacks.map((p) => ({
							label: p.name,
							value: object_ref_id(p._id)
						}))}
						onChange={(packId) => addRecipe(recipeId, packId)}
					/>
				);
			}
		}
	];

	return (
		<div style={cardStyle}>
			<div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 18 }}>
				<div style={sectionIconStyle}><CrownFilled style={{ fontSize: 16 }} /></div>
				<div>
					<div style={{ fontSize: 15, fontWeight: 700, color: '#3F3F3F' }}>
						{t('worlds.details.monetizationSection')}
					</div>
					<div style={{ fontSize: 12.5, color: '#A0A0A0' }}>
						{t('worlds.details.monetizationHint')}
					</div>
				</div>
			</div>

			<div style={{ fontSize: 13, fontWeight: 600, color: '#3F3F3F', marginBottom: 8 }}>
				{t('worlds.details.inPacksLabel')}
			</div>
			{worldPacks.length === 0
				?	<div style={{ fontSize: 12.5, color: '#8E8E8E', marginBottom: 12 }}>
						{t('worlds.details.notInAnyPack')}
					</div>
				:	<div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
						{worldPacks.map((pack) => (
							<div
								key={object_ref_id(pack._id)}
								style={{ display: 'flex', alignItems: 'center', gap: 10, border: '1px solid #EFEDEC', borderRadius: 10, padding: '8px 12px' }}
							>
								<Tag color='gold' style={{ marginRight: 0 }}>{pack.name}</Tag>
								<Typography.Text type='secondary' style={{ fontSize: 12, flex: 1 }}>
									{pack.value}
								</Typography.Text>
								<Popconfirm
									title={t('worlds.details.removeFromPackConfirm', { name: pack.name })}
									onConfirm={() => removeWorld(pack)}
								>
									<Button size='small' danger loading={package_update_loading}>
										{t('worlds.details.removeFromPack')}
									</Button>
								</Popconfirm>
							</div>
						))}
					</div>}

			<Space wrap style={{ marginBottom: 20 }}>
				<Select
					style={{ minWidth: 220 }}
					placeholder={t('worlds.details.addToPackPlaceholder')}
					value={selectedWorldPack}
					options={worldPackOptions}
					onChange={setSelectedWorldPack}
					allowClear
				/>
				<Button onClick={addWorld} disabled={!selectedWorldPack}>
					{t('worlds.details.addToPack')}
				</Button>
				<Button type='dashed' icon={<PlusOutlined />} onClick={openWorldPackForm}>
					{t('worlds.details.createPackWithWorld')}
				</Button>
			</Space>

			<div style={{ fontSize: 13, fontWeight: 600, color: '#3F3F3F', marginBottom: 8 }}>
				{t('worlds.details.recipesInPacksLabel')}
			</div>
			<Table
				size='small'
				rowKey={(record) => object_ref_id(record._id)}
				columns={recipeColumns}
				dataSource={worldRecipes}
				pagination={false}
				rowSelection={{
					selectedRowKeys: selectedRecipes,
					onChange: setSelectedRecipes
				}}
			/>
			<div style={{ marginTop: 10 }}>
				<Button
					type='dashed'
					icon={<PlusOutlined />}
					disabled={selectedRecipes.length === 0}
					onClick={openRecipePackForm}
				>
					{t('worlds.details.createRecipePackCount', { count: selectedRecipes.length })}
				</Button>
			</div>

			<PackageForm
				messageApi={messageApi}
				isModalOpen={packFormOpen}
				setIsModalOpen={setPackFormOpen}
				update={false}
				packageToUpdate={{}}
				setPackageToUpdate={() => {}}
				pageSize={0}
				skip={0}
				preset={packPreset}
			/>
		</div>
	);
};

WorldPacksCard.propTypes = {
	messageApi: PropTypes.object.isRequired,
	worldId: PropTypes.string.isRequired
};

export default WorldPacksCard;
